import { AlertType } from '@/alert/components/alertDetail/alertDetail.types';
import { useTargetDetailProvide } from '@/alert/components/alertTargetList/alertTargetList.uses';
import { TARGET_TYPE, useAlertTagsAndTargets } from '@/alert/utils/tagsAndTargets.uses';
import { getAlertType } from '@/alert/utils/utils';
import type {
  DetailItemInfo,
  ItemInfo,
  RouteInfo,
  SlideViewType,
  StoredAlertParams,
} from '@/common/components/organisms/alertListDetail/alertListDetail.types';
import { FRAME_NAMES } from '@/common/define/apiTrace.define';
import { ROLE_PERMISSION_KEY } from '@/common/define/rolePermission.define';
import { useInternational } from '@/common/locale';
import { useRolePermission } from '@/common/permission/permission.utils';
import { handleResize } from '@/common/utils/browserPopupUtils';
import { utcZeroTimeToStandardTime } from '@/common/utils/commonUtils';
import { webStorageController } from '@/common/utils/browser';
import {
  DATABASE_ALERT_PLATFORM_TYPES,
  useOverviewAlertStore,
} from '@/database/stores/overview-alert';
import { useRepeat } from '@/worker/composables/useRepeat';
import dayjs from 'dayjs';
import { isEmpty, isNil } from 'lodash-es';
import { storeToRefs } from 'pinia';
import { computed, ComputedRef, onBeforeUnmount, ref, Ref, watch } from 'vue';
import { AlertInfoType } from '@/common/components/organisms/overviewAlert/overviewAlert.setup';
import { useAlertGroupStore } from '@/alert/stores/alert-group';
import {
  getV9AlertsRules,
  postV9AlertsDashboardAlertGroupRulesByDashboardId,
} from '@/api-client/alert/queries/alert-common-v9-controller/alert-common-v9-controller';
import {
  postV11AlertsAlertGroupCountFiltered,
  postV11AlertsAlertGroupRulesFiltered,
} from '@/api-client/alert/queries/alert-group-filtered-controller/alert-group-filtered-controller';
import type { GetV9AlertsRulesParams } from '@/api-client/alert/model';
import type { SlideFilter } from './alertListDetailSlide.setup';
import { useGetDashboardAlertCountData } from '@/common/api/alert';
import { useMutation } from '@tanstack/vue-query';
import { usePostV9AlertsAlertRulesClear } from '@/api-client/alert/queries/alert-rule-v9-controller/alert-rule-v9-controller';
import { useAbortApi } from '@/common/utils/apiUtils';
import { useMessage } from '@/common/inject/useMessage';
import { isAxiosError } from 'axios';
import { AlertCountItem } from '@/api-client/alert/model';
import { isCancelError, isIgnoreRequestError } from '@/common/utils/error.utils';
import { useDashboardWidgetsAlertStore } from '@/dashboard/stores/dashboard-widgets-alert';
import {
  FRAME_KEY,
  POPUP_MIN_SIZE,
  SYSTEM_ALERT_GROUP_ID,
  USE_SLIDE_VIEW,
} from './alertListDetail.define';
import { normalizeStoredKeys, normalizeType } from './alertListDetail.utils';
import { useVueQueryErrorHandler } from '@/common/composables/useVueQueryErrorHandler';

interface AlertRulesRequest {
  alertGroupId?: string;
  platformTypes?: string[];
  limit?: number;
  targetIds?: string[];
  frameName?: string;
  isPolling?: boolean;
  isTimeout?: boolean;
  signal?: AbortSignal;
}

const useGetAlertRules = () => {
  const { mutateAsync } = useMutation({
    mutationFn: async ({
      alertGroupId,
      platformTypes,
      limit,
      targetIds,
      frameName,
      isPolling,
      isTimeout,
      signal,
    }: AlertRulesRequest) => {
      const params: GetV9AlertsRulesParams = {
        alertGroupId,
        platformTypes: platformTypes as GetV9AlertsRulesParams['platformTypes'],
        limit,
        targetIds,
      };

      return getV9AlertsRules(
        params,
        {
          params: {
            frameName,
            isPolling,
            isTimeout,
          },
        },
        signal,
      );
    },
  });

  return { getAlertRules: mutateAsync };
};

export interface Props {
  type?: SlideViewType;
  isShow?: boolean;
  viewName?: string;
  alertInfo?: AlertInfoType;
  filter?: SlideFilter;
}
export interface Emit {
  (e: 'expand-slide', value: boolean): void;
  (e: 'openDetail'): void;
}

const useClearAlert = (routeInfo: Ref<RouteInfo | undefined>) => {
  const type = routeInfo.value?.type ?? USE_SLIDE_VIEW.DASHBOARD;
  const frameNameMap = FRAME_NAMES[FRAME_KEY[type]] as Record<string, string>;

  // User alert clear mutation
  const userFrameName = frameNameMap?.CLEAR_ALERT_RULE;
  const { mutateAsync: clearUserAlertMutate } = usePostV9AlertsAlertRulesClear({
    request: {
      params: {
        frameName: userFrameName,
        isTimeout: false,
      },
    },
  });

  // System alert clear mutation
  const systemFrameName = frameNameMap?.CLEAR_SYSTEM_RULE;
  const { mutateAsync: clearSystemAlertMutate } = usePostV9AlertsAlertRulesClear({
    request: {
      params: {
        frameName: systemFrameName,
        isTimeout: false,
      },
    },
  });

  /**
   * 알림 룰 클리어 요청 데이터 생성
   * @param items - 클리어할 아이템 목록
   * @returns 클리어 요청 배열
   */
  const createClearRequests = (items: DetailItemInfo[]) => {
    return items.flatMap((item) => {
      return (
        item.targetList?.map((v) => ({
          alertRuleId: item.id,
          targetId: v.id,
          subName: item.subName,
        })) ?? []
      );
    });
  };

  /**
   * 공통 알림 클리어 처리
   * @param items - 클리어할 아이템 목록
   * @param alertType - 알림 타입 ('user' | 'system')
   */
  const clearAlerts = async (items: DetailItemInfo[], alertType: AlertType) => {
    if (!items?.length) {
      return;
    }

    const clearRequests = createClearRequests(items);

    if (alertType === 'user') {
      await clearUserAlertMutate({ data: clearRequests });
    } else {
      await clearSystemAlertMutate({ data: clearRequests });
    }
  };

  const clearController = {
    user: async (items: DetailItemInfo[]) => {
      await clearAlerts(items, 'user');
    },
    system: async (items: DetailItemInfo[]) => {
      await clearAlerts(items, 'system');
    },
  };

  return {
    clearController,
  };
};

const useAlertGroupListForBrowserPopup = (routerInfo: Ref<RouteInfo | undefined>) => {
  const databaseStore = useOverviewAlertStore();
  const commonStore = useAlertGroupStore();

  const fetchGroupList = async (_routerInfo?: RouteInfo) => {
    const info = _routerInfo ?? routerInfo.value;
    if (info?.type === USE_SLIDE_VIEW.DASHBOARD) {
      return;
    }

    if (info?.type === USE_SLIDE_VIEW.DATABASE) {
      await databaseStore.fetchAlertGroupsCount({
        instanceIdsFromRoute: info?.[info.type]?.instanceIds || [],
      });
    } else {
      const alertInfo = info?.[info.type]?.alertInfo ?? {};
      if (alertInfo) {
        await commonStore.fetchAlertGroupList(
          alertInfo.frameName,
          alertInfo.platformTypes,
          alertInfo.targetIds,
        );
      }
    }
  };

  const resetGroupStore = () => commonStore.reset();

  return {
    fetchGroupList,
    resetGroupStore,
  };
};

const useAlertRule = (
  routeInfo: Ref<RouteInfo | undefined>,
  getSignal: () => AbortSignal,
  effectiveFilter: ComputedRef<SlideFilter | undefined>,
  effectiveAlertInfo: ComputedRef<AlertInfoType | undefined>,
) => {
  const { t } = useInternational();
  const { getDashboardAlertCountData } = useGetDashboardAlertCountData();
  const { getAlertRules } = useGetAlertRules();

  const selectedGroupId = ref(SYSTEM_ALERT_GROUP_ID);
  const selectedRuleCardId = ref('');

  const allGroupRuleData = ref<Map<string, DetailItemInfo[]>>(new Map());
  const isFetchingGroupList = ref(false);
  const groupList = ref<ItemInfo[] | undefined>();
  const ruleList = computed<DetailItemInfo[]>(
    () => allGroupRuleData.value.get(selectedGroupId.value) ?? [],
  );
  const overviewAlertStore = useOverviewAlertStore();

  const { groupList: overviewGroupList } = storeToRefs(overviewAlertStore);
  const { groupList: alertGroupList } = storeToRefs(useAlertGroupStore());

  const { tagsAndTargetsController } = useAlertTagsAndTargets();

  const getConvertRuleList = (list): DetailItemInfo[] => {
    return list.map((item) => {
      // triggerTime: 연도 포함 표준시각(시간창 조회용), collectTime: 화면 표시용(연도 생략)
      const triggerTime = item.triggerTime ? utcZeroTimeToStandardTime(item.triggerTime) : '';
      const collectTime = triggerTime ? dayjs(triggerTime).format('MM-DD HH:mm:ss') : '';
      const type = getAlertType(item.alertRuleType);
      const getIdByTagId = () => item.targets.map((v) => `${v.targetId}`).join('//');

      return {
        cardId: `${item.alertRuleId}//${getIdByTagId()}//${item.subName}`,
        id: item.alertRuleId,
        name: item.alertRuleName,
        collectTime,
        targetList: tagsAndTargetsController[TARGET_TYPE.TARGETS](item.targets)?.targets,
        level: item.alertLevel?.toLowerCase(),
        type,
        ruleCriteria: item.ruleCriteria,
        subName: item.subName,
        resultId: item.resultId,
        eventType: item.eventType,
        alertRuleType: item.alertRuleType,
        triggerTime,
      };
    });
  };
  const setDashboardRuleListByAlertGroupId = async (alertGroupId: string, signal: AbortSignal) => {
    const { dashboardId, widgetField, userTags } = routeInfo.value?.dashboard ?? {};

    try {
      if (isNil(dashboardId)) {
        throw new Error('dashboardId is nil');
      }
      const {
        data: { data: dashboardRuleData = [] },
      } = await postV9AlertsDashboardAlertGroupRulesByDashboardId(
        dashboardId,
        { userTags, ...(widgetField && { widgetField }) },
        { alertGroupId, limit: 5000 },
        {
          params: {
            frameName: FRAME_NAMES[FRAME_KEY[USE_SLIDE_VIEW.DASHBOARD]].ALERT_RULE_LIST,
            isPolling: true,
          },
        },
        signal,
      );
      allGroupRuleData.value.set(
        selectedGroupId.value || SYSTEM_ALERT_GROUP_ID,
        getConvertRuleList(dashboardRuleData),
      );
    } catch (e) {
      if (isIgnoreRequestError(e) || isCancelError(e)) {
        return;
      }

      console.log(e);
    }
  };

  // filter 연계(apmOverview 등) rules — 대시보드 setDashboardRuleListByAlertGroupId 와 동형.
  // 그룹별 rules 를 rules/filtered(body=filter)로 직접 조회. (공유 store 미사용)
  const setFilteredRuleListByAlertGroupId = async (alertGroupId: string, signal: AbortSignal) => {
    if (!effectiveFilter.value) {
      return;
    }
    const type = routeInfo.value?.type;
    const frameNames = type ? FRAME_NAMES[FRAME_KEY[type]] : undefined;
    const frameName =
      frameNames && 'ALERT_GROUP_AND_RULES' in frameNames ? frameNames.ALERT_GROUP_AND_RULES : '';
    try {
      const {
        data: { data: ruleData = [] },
      } = await postV11AlertsAlertGroupRulesFiltered(
        effectiveFilter.value,
        { alertGroupId, limit: 5000 },
        { params: { frameName, isPolling: true, isTimeout: false } },
        signal,
      );
      allGroupRuleData.value.set(
        alertGroupId || SYSTEM_ALERT_GROUP_ID,
        getConvertRuleList(ruleData),
      );
    } catch (e) {
      if (isIgnoreRequestError(e) || isCancelError(e)) {
        return;
      }

      console.log(e);
    }
  };

  const setGroupList = () => {
    // filter 연계 시 그룹목록은 count/filtered 로 직접 채우므로(공유 store 미사용) 여기서 덮어쓰지 않음.
    if (effectiveFilter.value) {
      return;
    }
    const rawGroupList =
      (routeInfo.value?.type === USE_SLIDE_VIEW.DATABASE
        ? overviewGroupList.value
        : alertGroupList.value) || [];
    groupList.value = rawGroupList.map((curr) => ({
      id: curr.alertGroupId ?? SYSTEM_ALERT_GROUP_ID,
      name: curr.name === 'System Alert' ? t('WORD.SYSTEM_ALERT') : (curr.name ?? ''),
      critical: curr.criticalCount ?? 0,
      warning: curr.warningCount ?? 0,
    }));
  };

  const setData = async (data: AlertCountItem[] = []) => {
    const isDashboard = routeInfo.value?.type === USE_SLIDE_VIEW.DASHBOARD;
    // filter 연계도 대시보드와 동형: count 응답으로 groupList 를 직접 채우고 selectedGroupId 를 보존한다.
    const isFiltered = !!effectiveFilter.value;

    if (isDashboard || isFiltered) {
      const hasWidgetUUId = !!routeInfo.value?.dashboard?.widgetField?.widgetUUId;
      groupList.value = (data || []).reduce((acc: ItemInfo[], curr) => {
        if (hasWidgetUUId && curr.name === 'System Alert') {
          return acc;
        }

        acc.push({
          id: curr.alertGroupId ?? SYSTEM_ALERT_GROUP_ID,
          name: curr.name === 'System Alert' ? t('WORD.SYSTEM_ALERT') : (curr.name ?? ''),
          critical: curr.criticalCount ?? 0,
          warning: curr.warningCount ?? 0,
        });

        return acc;
      }, []);

      const prevGroupId = selectedGroupId.value;
      const hasPrevId = prevGroupId && !!groupList.value?.find((item) => item.id === prevGroupId);
      const firstId = groupList.value?.[0]?.id ?? SYSTEM_ALERT_GROUP_ID;
      selectedGroupId.value = hasPrevId ? prevGroupId : firstId;

      // selectedGroupId가 변경되면 watch(selectedGroupId)에서 API를 호출하므로 중복 방지
      if (selectedGroupId.value === prevGroupId) {
        if (isFiltered) {
          setFilteredRuleListByAlertGroupId(selectedGroupId.value, getSignal());
        } else {
          setDashboardRuleListByAlertGroupId(selectedGroupId.value, getSignal());
        }
      }
    } else {
      const groupId = selectedGroupId.value || SYSTEM_ALERT_GROUP_ID;
      allGroupRuleData.value.set(groupId, getConvertRuleList(data));
    }
  };

  const getData = async (_routeInfo?: RouteInfo) => {
    if (groupList.value === undefined) {
      isFetchingGroupList.value = true;
    }

    const signal = getSignal();

    try {
      if (!_routeInfo) {
        throw new Error('fail');
      }

      const params = _routeInfo[_routeInfo.type];
      const frameKey = FRAME_KEY[_routeInfo.type];

      if (!frameKey) {
        throw new Error(`Invalid route type: ${_routeInfo.type}`);
      }

      const frameNames = FRAME_NAMES[frameKey];
      const frameName =
        'ALERT_GROUP_AND_RULES' in frameNames
          ? frameNames.ALERT_GROUP_AND_RULES
          : frameNames.USER_SESSION_INFO; // fallback for USER_MONITORING_ALERT_DETAIL

      if (effectiveFilter.value) {
        // filter 연계: count/filtered 로 그룹목록(count)을 직접 조회 → setData 가 groupList 빌드 + 선택 보존.
        // rules 는 selectedGroupId watch / setData 에서 setFilteredRuleListByAlertGroupId 로 조회.
        const { data } = await postV11AlertsAlertGroupCountFiltered(
          effectiveFilter.value,
          { frameName, isPolling: true, isTimeout: false },
          signal,
        );
        setData(data?.data ?? []);
      } else if (_routeInfo.type === USE_SLIDE_VIEW.DASHBOARD) {
        const { dashboardId, userTags, widgetField } = params;
        const { data } = await getDashboardAlertCountData({
          dashboardId,
          request: { userTags, ...(widgetField && { widgetField }) },
          frameName,
          isPolling: true,
          isTimeout: false,
          signal,
        });
        setData(data?.data ?? []);
      } else if (_routeInfo.type === USE_SLIDE_VIEW.DATABASE) {
        // 디비 알람 그룹 리스트가 없으면 api호출 자체가 필요없음
        if (!groupList.value?.length) {
          isFetchingGroupList.value = false;
          return;
        }

        const { instanceIds, view, dbType } = params;
        const dbFrameName = `${frameName} - ${dbType} ${view}`;

        const { data } = await getAlertRules({
          frameName: dbFrameName,
          alertGroupId: selectedGroupId.value,
          platformTypes: DATABASE_ALERT_PLATFORM_TYPES,
          limit: 5000,
          targetIds: instanceIds,
          isPolling: true,
          isTimeout: false,
          signal,
        });
        setData(data?.data ?? []);
      } else {
        const { data } = await getAlertRules({
          frameName,
          alertGroupId: selectedGroupId.value,
          // 공유 세션은 다른 슬라이드가 덮어쓸 수 있으므로 prop 우선(effectiveAlertInfo)으로 platformTypes 확보.
          platformTypes: effectiveAlertInfo.value?.platformTypes,
          limit: 5000,
          isPolling: true,
          isTimeout: false,
          signal,
        });
        setData(data?.data ?? []);
      }
    } catch (e: unknown) {
      if (isIgnoreRequestError(e) || isCancelError(e)) {
        return;
      }

      console.error(`[AlertListDetail] fetch alert data error: ${e}`);
    } finally {
      isFetchingGroupList.value = false;
    }
  };

  const initData = () => {
    allGroupRuleData.value.clear();
    groupList.value = undefined;
  };

  watch(() => [alertGroupList.value, overviewGroupList.value], setGroupList, { deep: true });

  return {
    selectedGroupId,
    isFetchingGroupList,
    groupList,
    selectedRuleCardId,
    ruleList,
    setDashboardRuleListByAlertGroupId,
    setFilteredRuleListByAlertGroupId,
    getData,
    initData,
  };
};

const useAlertParamStorage = (props: Props) => {
  const getRouteInfo = () => {
    const storedDataString = webStorageController.getItem({
      type: 'session',
      key: 'alertDetailParams',
    });
    const storedData: StoredAlertParams = JSON.parse(storedDataString ?? '{}');

    if (isEmpty(storedData)) return undefined;

    const normalized = normalizeStoredKeys(storedData);
    const type = normalizeType(props.type ?? normalized.type);

    return {
      ...normalized,
      type,
    } as RouteInfo;
  };
  return {
    getRouteInfo,
  };
};

const setup = (props: Props, emit: Emit) => {
  const { t } = useInternational();
  const { confirmMsg, showErrorMsg, showSuccessMsg } = useMessage();
  const overviewAlertStore = useOverviewAlertStore();
  const { abortApi, getSignal } = useAbortApi();
  const { errorHandlerWithMessage } = useVueQueryErrorHandler();

  useTargetDetailProvide(emit);
  const paramStorage = useAlertParamStorage(props);

  const routeInfo = ref<RouteInfo | undefined>(paramStorage.getRouteInfo());

  // 세션(alertDetailParams)은 '팝업 전송용'이다. 임베디드 슬라이드는 props 가 정본이므로 세션을 보지 않는다
  // — 같은 화면에 슬라이드가 여러 개(예: APM Overview 헤더 벨 + 차트 알람) 있으면 세션키를 공유해 교차오염될 수
  // 있으므로, 세션 폴백은 브라우저 팝업(props.viewName)일 때만 적용한다.
  const effectiveFilter = computed<SlideFilter | undefined>(
    () =>
      props.filter ??
      (props.viewName ? routeInfo.value?.[routeInfo.value.type]?.filter : undefined),
  );

  // 규칙 목록 조회의 platformTypes 도 동일 원칙 — 임베디드는 props.alertInfo, 팝업만 세션 폴백.
  const effectiveAlertInfo = computed<AlertInfoType | undefined>(
    () =>
      props.alertInfo ??
      (props.viewName ? routeInfo.value?.[routeInfo.value.type]?.alertInfo : undefined),
  );

  const { clearController } = useClearAlert(routeInfo);
  const { fetchGroupList, resetGroupStore } = useAlertGroupListForBrowserPopup(routeInfo);
  const {
    selectedGroupId,
    isFetchingGroupList,
    groupList,
    selectedRuleCardId,
    ruleList,
    getData,
    initData,
    setDashboardRuleListByAlertGroupId,
    setFilteredRuleListByAlertGroupId,
  } = useAlertRule(routeInfo, getSignal, effectiveFilter, effectiveAlertInfo);

  const { isPermissionDenied } = useRolePermission();

  const isBrowserPopup = computed(() => !!props.viewName);
  const isShowAlertDetail = ref(false);

  const showDetail = (isShow) => {
    isShowAlertDetail.value = isShow;
    emit('expand-slide', isShow);
    if (!isShow) {
      selectedRuleCardId.value = '';
    }
  };
  const onClickGroup = () => {
    showDetail(false);
  };
  const onClickRule = (item: DetailItemInfo | null) => {
    showDetail(!!item);
  };
  const onClickClear = ({ items, type }: { items: DetailItemInfo[]; type: AlertType }) => {
    const isDenied = isPermissionDenied({
      type: 'action',
      rolePermissionKey: ROLE_PERMISSION_KEY.MONITORING.MONITORING_ALERT_LIST_CLEAR,
    });
    if (isDenied) {
      return;
    }
    confirmMsg({
      msgStr: t('MESSAGE.CLEAR_ALERT'),
      okCallback: async () => {
        try {
          await clearController[type](items);

          switch (props.type) {
            case USE_SLIDE_VIEW.DATABASE:
              overviewAlertStore.resetFetch();
              break;
            case USE_SLIDE_VIEW.DASHBOARD:
            default:
              await getData(routeInfo.value);
              break;
          }
          showDetail(false);
          showSuccessMsg({
            msgStr: t('MESSAGE.CLEAR_ALERT_COMPLETE'),
          });
        } catch (e) {
          if (isAxiosError(e) && e.response?.status !== 406) {
            showErrorMsg({
              msgStr: t('MESSAGE.ALERT_CLEAR_FAIL'),
            });
            return;
          }
          errorHandlerWithMessage({ error: e });
        }
      },
    });
  };

  const onReFetch = () => {
    getData(routeInfo.value);
  };

  if (isBrowserPopup.value) {
    const popupResize = () => {
      handleResize(POPUP_MIN_SIZE.WIDTH, POPUP_MIN_SIZE.HEIGHT);
    };
    window.addEventListener('resize', popupResize);

    onBeforeUnmount(() => {
      window.removeEventListener('resize', popupResize);
    });
  }

  const getAlertDataByStorage = async () => {
    routeInfo.value = paramStorage.getRouteInfo();
    try {
      // filter 연계(effectiveFilter) 시엔 count/filtered 응답이 그룹목록을 직접 빌드하므로(setData) 비-filter 그룹조회는 생략.
      if (
        isBrowserPopup.value &&
        routeInfo.value?.type !== USE_SLIDE_VIEW.DASHBOARD &&
        !effectiveFilter.value
      ) {
        await fetchGroupList(routeInfo.value);
      }
      await getData(routeInfo.value);
    } catch (e) {
      if (!isIgnoreRequestError(e) && !isCancelError(e)) {
        console.log(e);
      }
    }
  };

  const { resetFetch: repeatGetAlertDataByStorage, clearFetch: clearGetAlertDataByStorage } =
    useRepeat(getAlertDataByStorage, 5_000, {
      isSetup: props.type === USE_SLIDE_VIEW.DASHBOARD || props.type === USE_SLIDE_VIEW.DATABASE,
      isImmediate: false,
    });

  const { updatedFetchFlag } = storeToRefs(overviewAlertStore);
  watch(
    () => [updatedFetchFlag.value, props.isShow],
    async () => {
      if (props.isShow) {
        await getAlertDataByStorage();
      }
    },
    { immediate: true },
  );

  const clickedAlertWidgetInfo = computed(
    () => useDashboardWidgetsAlertStore().clickedAlertWidgetInfo,
  );

  watch(
    () => (props.type === USE_SLIDE_VIEW.DASHBOARD ? clickedAlertWidgetInfo.value : null),
    (current, prev) => {
      if (!current || !prev) return;
      const { widgetId: currentWidgetId } = current;
      const { widgetId: prevWidgetId } = prev;
      // 클릭한 위젯이 변경되었을 때 데이터 다시 가져오기, 초기에는 호출 x
      if (props.isShow && currentWidgetId && prevWidgetId) {
        repeatGetAlertDataByStorage();
      }
    },
  );

  watch(
    () => props.isShow,
    async (isShow) => {
      if (!isShow) {
        selectedRuleCardId.value = '';
        initData();
        onClickRule(null);
      }

      if (isShow) {
        repeatGetAlertDataByStorage();
      } else {
        clearGetAlertDataByStorage();
      }
    },
    { immediate: true },
  );
  watch(
    (): [string, DetailItemInfo[]] => [selectedRuleCardId.value, ruleList.value],
    ([cardId, list]) => {
      if (!list?.length || !list?.find((item) => item.cardId === cardId)) {
        showDetail(false);
      }
    },
  );

  watch(selectedGroupId, async (groupId) => {
    abortApi();
    if (effectiveFilter.value && groupId) {
      // filter 연계: 그룹 선택 시 rules/filtered 만 조회(count 재조회 금지 — 대시보드와 동형).
      await setFilteredRuleListByAlertGroupId(groupId, getSignal());
    } else if (props.type !== USE_SLIDE_VIEW.DASHBOARD && groupId) {
      await getData(routeInfo.value);
    } else if (props.type === USE_SLIDE_VIEW.DASHBOARD) {
      await setDashboardRuleListByAlertGroupId(groupId, getSignal());
    }
  });

  onBeforeUnmount(() => {
    clearGetAlertDataByStorage();
    resetGroupStore();
  });

  return {
    isShowAlertDetail,
    selectedGroupId,
    isFetchingGroupList,
    groupList,
    selectedRuleCardId,
    ruleList,
    onClickGroup,
    onClickRule,
    onClickClear,
    onReFetch,
    t,
  };
};

export { POPUP_MIN_SIZE, setup };
